import { useCallback } from 'react';
import useSWR, { SWRResponse, Key, useSWRConfig } from 'swr';
import { AxiosError } from 'axios';

import {
  ReservationFlowPaymentMethodListTicketBook,
  ReservationFlowTicketBookAvailableReservationCount,
} from '../../../../interfaces/reservation_flow';
import { useClientSideApiClient } from '../../../../openapi/axiosClient';
import { captureException } from '@sentry/nextjs';

export interface UseGetReservationFlowTicketBookAvailableReservationCountDataParams {
  parameter?: {
    merchantPublicId: string;
    paymentMethodTicketBook: ReservationFlowPaymentMethodListTicketBook;
    checkin: string;
  };
  isDisabled?: boolean;
}

export type UseGetReservationFlowTicketBookAvailableReservationCountDataValue =
  SWRResponse<ReservationFlowTicketBookAvailableReservationCount[], AxiosError>;

export const getGetReservationFlowTicketBookAvailableReservationCountDataKey: (params: {
  parameter: UseGetReservationFlowTicketBookAvailableReservationCountDataParams['parameter'];
}) => Key = ({ parameter }) => {
  return {
    path: '/api/reservation_flow/merchants/{merchant_public_id}/ticket_books/{ticket_book_public_id}/available_reservation_count',
    parameter,
  };
};

export const useGetReservationFlowTicketBookAvailableReservationCountData: (
  params: UseGetReservationFlowTicketBookAvailableReservationCountDataParams,
) => UseGetReservationFlowTicketBookAvailableReservationCountDataValue = ({
  parameter,
  isDisabled,
}) => {
  const apiClient = useClientSideApiClient();
  const key =
    !isDisabled && typeof parameter !== 'undefined'
      ? getGetReservationFlowTicketBookAvailableReservationCountDataKey({
          parameter,
        })
      : null;

  const fetcher: () => Promise<
    ReservationFlowTicketBookAvailableReservationCount[]
  > = async () => {
    if (typeof parameter === 'undefined') {
      captureException('Never');
      throw new Error('Never');
    }

    const { merchantPublicId, paymentMethodTicketBook, checkin } = parameter;

    const ticketBookPublicIds = paymentMethodTicketBook.products.flatMap(
      (product) => {
        return product.available_ticket_books.map((availableTicketBook) => {
          return availableTicketBook.public_id;
        });
      },
    );

    try {
      return await Promise.all(
        ticketBookPublicIds.map(async (publicId) => {
          const value =
            await apiClient.getReservationFlowTicketBookReservationAvailableCount(
              {
                parameter: {
                  merchant_public_id: merchantPublicId,
                  ticket_book_public_id: publicId,
                  checkin,
                },
              },
            );

          return {
            checkin,
            publicId,
            value,
          };
        }),
      );
    } catch (error) {
      throw error;
    }
  };

  return useSWR(key, fetcher);
};

export type UseClearAllGetReservationFlowTicketBookAvailableReservationCountDataParams =
  {
    parameter: {
      merchantPublicId: NonNullable<
        UseGetReservationFlowTicketBookAvailableReservationCountDataParams['parameter']
      >['merchantPublicId'];
    };
  };

export type UseClearAllGetReservationFlowTicketBookAvailableReservationCountDataValue =
  {
    clearAllGetReservationFlowTicketBookAvailableReservationCountData: () => Promise<void>;
  };

export const useClearAllGetReservationFlowTicketBookAvailableReservationCountData: (
  params: UseClearAllGetReservationFlowTicketBookAvailableReservationCountDataParams,
) => UseClearAllGetReservationFlowTicketBookAvailableReservationCountDataValue = ({
  parameter: { merchantPublicId },
}) => {
  const { mutate } = useSWRConfig();

  const clearAllGetReservationFlowTicketBookAvailableReservationCountData =
    useCallback(async () => {
      await mutate(
        (key) => {
          if (!key || typeof key !== 'object') {
            return false;
          }

          if (
            !('path' in key) ||
            !('parameter' in key) ||
            !('merchantPublicId' in key.parameter)
          ) {
            return false;
          }

          return (
            key.path ===
              '/api/reservation_flow/merchants/{merchant_public_id}/ticket_books/{ticket_book_public_id}/available_reservation_count' &&
            key.parameter.merchantPublicId === merchantPublicId
          );
        },
        undefined,
        { revalidate: false },
      );
    }, [merchantPublicId, mutate]);

  return { clearAllGetReservationFlowTicketBookAvailableReservationCountData };
};
